import { useMutation, useQuery } from '@tanstack/react-query';
import { orpc, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import type {
  ListEntityAssociationsInput,
  CreateEntityAssociationInput,
  DeleteEntityAssociationInput,
  ConfirmMatchInput,
} from '@erp/rpc';

export function useEntityAssociationsList(input: ListEntityAssociationsInput) {
  return useQuery(orpc.core.entityAssociations.list.queryOptions({ input }));
}

export function useCreateEntityAssociation() {
  const invalidateQueries = useInvalidateRpcQueryFn((orpc) => [
    orpc.core.entityAssociations.list.key(),
    orpc.accountsPayable.invoices.get.key(),
  ]);

  return useMutation(
    orpc.core.entityAssociations.create.mutationOptions({
      onSuccess: () => {
        invalidateQueries();
      },
    }),
  );
}

export function useConfirmEntityAssociationMatch() {
  const invalidateQueries = useInvalidateRpcQueryFn((orpc) => [
    orpc.core.entityAssociations.list.key(),
    orpc.accountsPayable.invoices.get.key(),
    orpc.accountsPayable.invoices.list.key(),
  ]);
  const { showToast } = useToast();

  return useMutation(
    orpc.core.entityAssociations.confirmMatch.mutationOptions({
      onSuccess: (data) => {
        if (!data.success) {
          showToast({
            message: 'Match could not be confirmed — it may have been removed',
            color: 'error',
          });
          return;
        }
        invalidateQueries();
      },
    }),
  );
}

export function useDeleteEntityAssociation() {
  const invalidateQueries = useInvalidateRpcQueryFn((orpc) => [
    orpc.core.entityAssociations.list.key(),
    orpc.accountsPayable.invoices.get.key(),
  ]);

  return useMutation(
    orpc.core.entityAssociations.delete.mutationOptions({
      onSuccess: () => {
        invalidateQueries();
      },
    }),
  );
}
